/**
 * プロンプト入力モードの「モーショングラフィックス」(レイヤー素材をそのまま動かす第 3 の生成方法)。
 * 計画: docs/features/motion-graphics-mode-plan.md。ここは 3 タブ目の表示条件・設定値・素材の受け入れ規則を
 * 純関数で持つ。生成の body と前提の検証は layeredGenerate.ts、availability は availability.ts。
 */
import { isLayerStackMaterial } from '../../components/generation/state/assetMediaUtils';
import { isSvgContentType } from '../../components/materials/shared/materialUtils';
import type { GenerationAsset } from '../../components/generation/state/types';
import type { MotionLayeredAvailability } from './availability';

/**
 * 3 タブ目「モーショングラフィックス」を出すか (計画 md §5.1)。
 *
 * ブランド設定の `features.motionGraphicsPromptMode === true` **かつ** backend の暫定ゲートを通る
 * (`GET /api/motion-layered/availability` の結果があり reason が 'restricted' でない) ときに出す。
 * 料金行なし (rate_not_configured) はタブを出したまま設定欄に理由を出して生成ボタンを無効にする。
 * レンダラーの状態は見ない (描画は生成を押した後・利用者決定 2026-10-01)。
 * 取得中・取得に失敗した (fetch_failed) は暫定ゲートを判断できないので出さない (fail-closed)。
 */
export function shouldShowMotionGraphicsTab({
  flag,
  availability,
}: {
  /** brandConfig.features.motionGraphicsPromptMode */
  flag: boolean | undefined;
  /** useMotionLayeredAvailability の結果 (取りに行かない・取得中は null) */
  availability: MotionLayeredAvailability | null | undefined;
}): boolean {
  if (flag !== true || !availability) return false;
  return availability.enabled || availability.reason === 'rate_not_configured';
}

/** 尺 (秒)。ブランドカット (motion) と同じ 2〜16 秒。小数可 (計画 md §5.3) */
export const MOTION_LAYERED_DURATION_MIN = 2;
export const MOTION_LAYERED_DURATION_MAX = 16;
export const MOTION_LAYERED_DURATION_DEFAULT = 6;

/** 比率 6 種 (ブランドカット motion と同じ・計画 md §5.3)。既定 16:9 */
export const MOTION_LAYERED_ASPECT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '16:9', label: '16:9（横）' },
  { value: '9:16', label: '9:16（縦）' },
  { value: '1:1', label: '1:1（正方形）' },
  { value: '4:3', label: '4:3' },
  { value: '3:4', label: '3:4' },
  { value: '21:9', label: '21:9（シネマ）' },
];
export const MOTION_LAYERED_ASPECT_DEFAULT = '16:9';

function aspectValue(value: string): number | null {
  const [w, h] = value.split(':').map(Number);
  return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? w / h : null;
}

/** カンバスの縦横比に最も近い比率 (6 種・対数での距離)。寸法が読めなければ既定 (計画 md §5.3「カンバスから自動選択」) */
export function closestMotionLayeredAspect(width: number, height: number): string {
  if (!(width > 0) || !(height > 0)) return MOTION_LAYERED_ASPECT_DEFAULT;
  const target = Math.log(width / height);
  let best = MOTION_LAYERED_ASPECT_DEFAULT;
  let bestDist = Infinity;
  for (const opt of MOTION_LAYERED_ASPECT_OPTIONS) {
    const v = aspectValue(opt.value);
    if (v === null) continue;
    const dist = Math.abs(Math.log(v) - target);
    if (dist < bestDist) {
      best = opt.value;
      bestDist = dist;
    }
  }
  return best;
}

export const MOTION_LAYERED_ASPECT_MISMATCH_NOTICE = 'カンバスと比率が違うため余白ができます';
/** 比率の差がこれ以下なら同じとみなす (丸めで 1920×1080 と 16:9 がわずかにずれても注意しない) */
const ASPECT_TOLERANCE = 0.01;

/** 選んだ比率がカンバスと違う (= 余白ができる) ときの注意。同じ・カンバスが無ければ null */
export function motionLayeredCanvasAspectNotice(
  aspectRatio: string,
  canvas: { width: number; height: number } | null | undefined,
): string | null {
  if (!canvas || !(canvas.width > 0) || !(canvas.height > 0)) return null;
  const chosen = aspectValue(aspectRatio);
  if (chosen === null) return null;
  const actual = canvas.width / canvas.height;
  return Math.abs(chosen - actual) / actual > ASPECT_TOLERANCE ? MOTION_LAYERED_ASPECT_MISMATCH_NOTICE : null;
}

/** 効果音 (なし / 自動)。値は backend の `sound_effects` と同じ */
export type MotionLayeredSoundEffects = 'none' | 'auto';
/** 終了時の状態。`composite` = 入稿デザインと同じ (既定) / `free` = 自由 (計画 md §4.2 の endState) */
export type MotionLayeredEndState = 'composite' | 'free';

/** 3 タブ目の設定 (layeredSettingsStore が持ち、生成の body に載せる) */
export type MotionLayeredSettings = {
  durationSeconds: number;
  aspectRatio: string;
  soundEffects: MotionLayeredSoundEffects;
  endState: MotionLayeredEndState;
};

export const DEFAULT_MOTION_LAYERED_SETTINGS: MotionLayeredSettings = {
  durationSeconds: MOTION_LAYERED_DURATION_DEFAULT,
  aspectRatio: MOTION_LAYERED_ASPECT_DEFAULT,
  soundEffects: 'none',
  endState: 'composite',
};

/**
 * 尺の入力を 2〜16 秒に収め、入力欄の step (0.1) に合わせて小数 1 桁へ丸める。
 * 空文字・数でない入力は null (呼び出し側で直前の値に戻す)。
 */
export function clampMotionLayeredDuration(value: string | number): number | null {
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  const clamped = Math.min(MOTION_LAYERED_DURATION_MAX, Math.max(MOTION_LAYERED_DURATION_MIN, n));
  return Math.round(clamped * 10) / 10;
}

export const MOTION_LAYERED_LAYER_REQUIRED_NOTICE =
  'モーショングラフィックスにはレイヤー素材が必要です (SVG をアップロードしてください)';
export const MOTION_LAYERED_SINGLE_MATERIAL_NOTICE = 'モーショングラフィックスでは素材は 1 件です';

/**
 * 選択済み素材に対する注意文 (計画 md §3.4・§5.2)。問題が無ければ null。
 *
 * - レイヤー素材が 1 件も無い、または通常の画像 / 動画 / 音声が混じっている → 「レイヤー素材が必要です」
 * - レイヤー素材が 2 件以上 → 「素材は 1 件です」
 *
 * 2 件目のレイヤー素材の追加そのものは assetsContext の addAsset が弾く (shouldRejectExtraLayerMaterial)。
 * 通常の画像は追加を弾かず、この注意文と生成ボタンの無効化で知らせる。
 */
/**
 * レイヤー構成を「使うときに作る」対象の素材か: SVG 原本で、まだ入稿解析 (layer_stack) を通っていないもの
 * (ブランド管理のロゴ複製・通常アップロードで入った SVG)。生成ボタン押下時に `POST .../layers` で解析してから生成する
 * (docs/features/material-representations-roadmap.md)。
 */
export function isMotionLayerCandidate(asset: Pick<GenerationAsset, 'layerStack' | 'contentType'>): boolean {
  return isSvgContentType(asset.contentType) && !isLayerStackMaterial(asset);
}

export function getMotionLayeredAssetNotice(
  assets: ReadonlyArray<Pick<GenerationAsset, 'layerStack' | 'contentType'>>,
): string | null {
  // レイヤー素材 (解析済み) と、押下時に解析する SVG 候補のどちらも「レイヤー素材」として数える
  const layerCount = assets.filter((a) => isLayerStackMaterial(a) || isMotionLayerCandidate(a)).length;
  if (layerCount === 0 || layerCount < assets.length) return MOTION_LAYERED_LAYER_REQUIRED_NOTICE;
  if (layerCount > 1) return MOTION_LAYERED_SINGLE_MATERIAL_NOTICE;
  return null;
}

/** トップページから motion のまま自動生成で来たときの案内 (resolvePromptAutoGenerateAction の 'motionGuide') */
export const MOTION_LAYERED_AUTO_GENERATE_NOTICE =
  'モーショングラフィックスはレイヤー素材 (SVG) を選んでから「モーショングラフィックスを生成」を押してください';

/**
 * トップページから `state.autoGenerate` で来たときの自動生成の扱い (ScenarioGenerator)。
 *
 * - 'generate': 従来どおり 1 度だけ生成する
 * - 'motionGuide': タブが出ている状態で motion を選んでいる。motion はレイヤー素材 1 件が必須で、
 *   トップページからはレイヤー素材を選んで来られない (選択済み素材の確認・レイヤーの固定もまだ) ため、
 *   自動では生成せず、autoGenerate を消費して (二度と走らせない) 案内を出す。待つと後で AIおまかせ を
 *   押した瞬間に意図しない生成が走るため。定額のポイントを利用者の確認なしに使わない意味もある
 * - 'wait': motion だがタブを出さない条件 (availability の取得中・使えない) で、reference へ寄せられる途中。
 *   寄せた後に従来どおり生成する
 */
export type PromptAutoGenerateAction = 'generate' | 'motionGuide' | 'wait';

export function resolvePromptAutoGenerateAction({
  videoGenerationMode,
  motionTabVisible,
}: {
  videoGenerationMode: string | null;
  motionTabVisible: boolean;
}): PromptAutoGenerateAction {
  if (videoGenerationMode !== 'motion') return 'generate';
  return motionTabVisible ? 'motionGuide' : 'wait';
}

/**
 * 2 件目のレイヤー素材の追加を弾くか (計画 md §5.2「motion で受け付けるのはレイヤー素材 1 件だけ」)。
 *
 * 静止画タブでは productionMode='prompt' / promptVideoGenerationMode='motion' が動画タブから
 * 残ったままになり、選択済み素材のスコープも共有している (STILL_IMAGE_SCOPE)。モードの値だけで
 * 判定すると静止画タブでレイヤー素材 (中身は合成 PNG) を 2 枚選べなくなるため、
 * (1) 画面上で motion が有効 (GenerationWorkspace の isPromptMotionMode = 動画タブ + プロンプト入力 +
 * motion + タブの表示条件・レイヤー一覧の表示条件と同じ値) (2) 追加する素材がレイヤー素材
 * (3) 選択済み素材に別のレイヤー素材が既にある、をすべて必須にする。
 * URL のパスでは判定しない (サブパス配信 `VITE_APP_BASENAME=/app` で外れるため)。
 */
export function shouldRejectExtraLayerMaterial({
  promptMotionModeActive,
  current,
  asset,
}: {
  promptMotionModeActive: boolean;
  current: ReadonlyArray<Pick<GenerationAsset, 'id' | 'layerStack'>>;
  asset: Pick<GenerationAsset, 'id' | 'layerStack'>;
}): boolean {
  if (!promptMotionModeActive) return false;
  if (!isLayerStackMaterial(asset)) return false;
  return current.some((a) => a.id !== asset.id && isLayerStackMaterial(a));
}
